import type { FileWithPreview } from '@models/types';
import type { Detector } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/face-detector-provider/types';

import { type PipelineResult, runFacePipeline } from './face-pipeline';
import { type ImageQualityResult, runImageQualityPipeline } from './image-quality-pipeline';

export type ValidatedLivenessFrame = {
    pipelineResult: PipelineResult;
    imageQualityResult: ImageQualityResult;
};

const loadImageFromFile = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load captured liveness frame'));
        };
        image.src = url;
    });

/**
 * Re-run face + IQA prechecks on the exact JPEG that will be uploaded.
 * Preview-loop success alone is not enough — the screenshot can differ.
 */
export const validateCapturedLivenessFrame = async ({
    file,
    detectorModel,
    landmarkModel,
    imageQualityModel,
}: {
    file: FileWithPreview | File;
    detectorModel: Detector;
    landmarkModel: Detector;
    imageQualityModel: Detector;
}): Promise<ValidatedLivenessFrame | null> => {
    try {
        const image = await loadImageFromFile(file);
        const pipelineResult = runFacePipeline(detectorModel, landmarkModel, image);

        if (
            !pipelineResult?.hasFace ||
            !pipelineResult.box ||
            !(pipelineResult.isCentered ?? false) ||
            !(pipelineResult.isPoseOk ?? false) ||
            pipelineResult.eyesClosed ||
            pipelineResult.isLowQuantity
        ) {
            return null;
        }

        const imageQualityResult = runImageQualityPipeline(
            imageQualityModel,
            image,
            pipelineResult.landmarkBox ?? pipelineResult.box,
        );

        if (!imageQualityResult || imageQualityResult.failedChecks.length > 0) {
            return null;
        }

        return { pipelineResult, imageQualityResult };
    } catch {
        return null;
    }
};
